import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { ROUTES } from "src/consts/routes";
import Page from "src/components/Layout/Page";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import FormHeader from "src/components/Displays/FormHeader";
import { CancelContractTypes } from "src/pages/ContractChange";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import FormContainer from "src/components/Displays/FormContainer";
import { SESSION_KEYS, SITE_TECHNICAL_DATA } from "src/consts/session";
import PaymentDetailsForm from "src/components/Forms/PaymentDetailsForm";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import { paymentDetailsFormSchema } from "src/schema/paymentDetailsFormSchema";
import { OEMs } from "src/hooks/useLeadTransfer";

export enum PaymentDetailsTypes {
  SEPA = "SEPA",
  BANK = "BANK",
  BLANK = "",
}

export interface PaymentDetailsFormValues {
  account?: PaymentDetailsTypes;
  cardHolder?: string;
  iban?: string;
  consent?: boolean;
}

const PaymentDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageTitle = t("page.paymentDetails.pageTitle");
  const formTitle = t("page.paymentDetails.formTitle");
  const formSubtitle = t("page.paymentDetails.formSubTitle");

  const [getSessionStorageItem, setSessionStorageItem] =
    useSessionStorage<SITE_TECHNICAL_DATA>(SESSION_KEYS.SITE_TECHNICAL_DATA);

  const [getOemSelected] = useSessionStorage<string>(
    SESSION_KEYS.SELF_ONBOARDING_OEM_SELECTED,
  );

  const form = useForm<PaymentDetailsFormValues>({
    defaultValues: {
      account:
        (getSessionStorageItem()?.paymentDetails
          ?.account as PaymentDetailsTypes) || PaymentDetailsTypes.BLANK,
      cardHolder: getSessionStorageItem()?.paymentDetails?.cardHolder || "",
      iban: getSessionStorageItem()?.paymentDetails?.iban || "",
      consent: getSessionStorageItem()?.paymentDetails?.consent || false,
    },
    mode: "onSubmit",
    resolver: yupResolver(paymentDetailsFormSchema),
  });

  const handleBackClick = () => {
    const cancelContractType = getSessionStorageItem()?.cancelContractType;
    if (cancelContractType && cancelContractType === CancelContractTypes.SELF) {
      navigate(ROUTES.CONTRACT_CHANGE);
      return;
    }
    navigate(ROUTES.DATE_OF_CONTRACT_START);
  };

  const onSubmit = (data: PaymentDetailsFormValues) => {
    const sessionData = getSessionStorageItem();
    const oemSelected = getOemSelected();

    const updatedSessionData: SITE_TECHNICAL_DATA = {
      ...sessionData,
      paymentDetails: {
        account: data.account,
        cardHolder: data.cardHolder?.trim(),
        iban: `${data.iban?.replaceAll(" ", "")}`,
        consent: data.consent,
      },
    };
    if (data.account === PaymentDetailsTypes.BANK) {
      delete updatedSessionData.paymentDetails?.cardHolder;
      delete updatedSessionData.paymentDetails?.iban;
      delete updatedSessionData.paymentDetails?.consent;
    }
    setSessionStorageItem(updatedSessionData);

    if (oemSelected === OEMs.SMA_HOMEMGR) {
      navigate(ROUTES.CONTRACT_SUMMARY_SMA);
      return;
    }
    navigate(ROUTES.CONTRACT_SUMMARY);
  };

  return (
    <Page pageTitle={pageTitle}>
      <FormContainer title={formTitle} onSubmit={form.handleSubmit(onSubmit)}>
        <Box
          sx={{
            my: 6,
          }}
        >
          <FormHeader text={formSubtitle} />
        </Box>
        <Box sx={{ marginTop: 6, marginBottom: 3 }}>
          <PaymentDetailsForm form={form} />
        </Box>
        <FormSubmitContainer onClick={handleBackClick} />
      </FormContainer>
    </Page>
  );
};

export default PaymentDetails;
